// Libs
import classNames from 'classnames/bind';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid2, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseCheckbox, BaseStatus } from '~/components/common';
// Others
import { EMPTY_STRING, EMPTY_VALUE } from '~/utils/constants/common';
import { IInventoryDetail } from '~/utils/interface/inventory';
import { formatCurrencyUSD, formatTitleCase } from '~/utils/helper/common';
// Styles, images, icons
import styles from './InfoProductDetail.module.scss';

type Props = {
  detailProduct?: IInventoryDetail;
};

const cx = classNames.bind(styles);

const InfoProductDetail = (props: Props) => {
  //#region Destructuring Props
  const { detailProduct } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation('admin/inventory');
  const [isDamageWaiver, setIsDamageWaiver] = useState<boolean>(false);
  const [isDiscountable, setIsDiscountable] = useState<boolean>(false);
  const [isTaxable, setIsTaxable] = useState<boolean>(false);
  const [isShowOnPrint, setIsShowOnPrint] = useState<boolean>(false);
  const [isAddOn, setsAddOn] = useState<boolean>(false);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!detailProduct) return;

    setIsDamageWaiver(detailProduct.isDamageWaiver);
    setIsDiscountable(detailProduct.isDiscountable);
    setIsTaxable(detailProduct.isTaxable);
    setIsShowOnPrint(detailProduct.isShowOnPrint);
    setsAddOn(detailProduct.isAddOn);
  }, [detailProduct]);
  //#endregion Implement Hook

  //#region Handle Function
  const renderValueItem = (label: string, value: string) => {
    return (
      <Stack gap={'7px'}>
        <Typography className={cx('label')}>{label}</Typography>
        <Typography className={cx('value')}>{value}</Typography>
      </Stack>
    );
  };

  const renderValueItemRow = (label: string, value: string) => {
    return (
      <Stack direction={'row'} gap={2} justifyContent={'space-between'}>
        <Typography className={cx('labelRow')}>{label}</Typography>
        <Typography className={cx('valueRow')}>{value}</Typography>
      </Stack>
    );
  };
  //#endregion Handle Function

  return (
    <Grid2 id='infoProductDetailComponent' container spacing={6}>
      <Grid2 size={8} container spacing={6}>
        <Grid2 size={6} className={cx('itemBox')}>
          <Stack gap={5}>
            <Typography className={cx('textHeader')}>{t('inventory_detail_title_basic_information')}</Typography>

            {renderValueItem(t('inventory_detail_title_product_id'), detailProduct?.inventoryProductId || EMPTY_VALUE)}

            {renderValueItem(t('inventory_detail_title_product_name'), detailProduct?.name || EMPTY_VALUE)}

            {renderValueItem(t('inventory_detail_title_brand'), detailProduct?.brand || EMPTY_VALUE)}

            {renderValueItem(
              t('inventory_detail_title_product_designation'),
              formatTitleCase(detailProduct?.productDesignation || EMPTY_STRING) || EMPTY_VALUE
            )}

            {/* {renderValueItem(t('inventory_detail_title_inventory_quantity'), detailProduct?.quantity || EMPTY_VALUE)} */}
          </Stack>
        </Grid2>

        <Grid2 size={6} className={cx('itemBox')}>
          <Stack gap={5}>
            <Typography className={cx('textHeader')}>{t('inventory_detail_title_pricing_information')}</Typography>

            {/* <Stack gap={1}>
              <Stack gap={'7px'}>
                <Typography className={cx('label')}>{t('inventory_detail_title_rental_price')}</Typography>

                <Typography className={cx('valuePrice')}>
                  {formatCurrencyUSD({ value: detailProduct?.rentalPrice, showDecimal: true })}
                </Typography>
              </Stack>

              <Typography className={cx('subLabel')}>{t('inventory_detail_title_per_day')}</Typography>
            </Stack> */}

            {renderValueItem(
              t('inventory_detail_title_purchase_price'),
              formatCurrencyUSD({ value: detailProduct?.purchasePrice, showDecimal: true })
            )}

            {renderValueItem(
              t('inventory_detail_title_replacement_cost'),
              formatCurrencyUSD({ value: detailProduct?.replacementCosts, showDecimal: true })
            )}
          </Stack>
        </Grid2>

        <Grid2 size={6} className={cx('itemBox')}>
          <Stack gap={5}>
            <Typography className={cx('textHeader')}>{t('inventory_detail_title_physical_specifications')}</Typography>

            {renderValueItem(t('inventory_detail_title_weight'), detailProduct?.weight || EMPTY_VALUE)}

            {renderValueItem(t('inventory_detail_title_dimensions'), detailProduct?.dimensions || EMPTY_VALUE)}

            {renderValueItem(
              t('inventory_detail_title_warehouse_location'),
              detailProduct?.warehouseLocation || EMPTY_VALUE
            )}
          </Stack>
        </Grid2>

        <Grid2 size={6} className={cx('itemBox')}>
          <Stack gap={5}>
            <Typography className={cx('textHeader')}>{t('inventory_detail_title_rental_details')}</Typography>
            {renderValueItem(t('inventory_detail_title_time_capacity'), detailProduct?.timeCapacity || EMPTY_VALUE)}

            {renderValueItem(t('inventory_detail_title_internal_notes'), detailProduct?.note || EMPTY_VALUE)}

            <Stack gap={'7px'}>
              <Typography className={cx('label')}>{t('inventory_detail_title_availability_status')}</Typography>
              {detailProduct?.status ? <BaseStatus isDot label={detailProduct?.status} /> : EMPTY_VALUE}
            </Stack>
          </Stack>
        </Grid2>
      </Grid2>

      <Grid2 size={4} container spacing={6}>
        <Grid2 size={12} className={cx('itemBox')}>
          <Stack gap={5}>
            <Typography className={cx('textHeader')}>{t('inventory_detail_title_status_summary')}</Typography>

            {renderValueItemRow(
              t('inventory_detail_title_product_id_label'),
              detailProduct?.inventoryProductId || EMPTY_VALUE
            )}

            {renderValueItemRow(
              t('inventory_detail_title_designation_label'),
              formatTitleCase(detailProduct?.productDesignation || EMPTY_STRING) || EMPTY_VALUE
            )}

            {/* {renderValueItemRow(
              t('inventory_detail_title_location_label'),
              detailProduct?.warehouseLocation || EMPTY_VALUE
            )} */}

            {/* {renderValueItemRow(
              t('inventory_detail_title_time_capacity_label'),
              detailProduct?.timeCapacity || EMPTY_VALUE
            )} */}

            <Stack direction={'row'} gap={2} justifyContent={'space-between'}>
              <Typography className={cx('labelRow')}>{t('inventory_detail_title_availability_babel')}</Typography>

              {detailProduct?.status ? <BaseStatus isDot label={detailProduct?.status} /> : EMPTY_VALUE}
            </Stack>
          </Stack>
        </Grid2>
        <Grid2 size={12} className={cx('itemBox')}>
          <Stack gap={5}>
            <Typography className={cx('textHeader')}>{t('inventory_detail_title_product_options')}</Typography>

            <BaseCheckbox label={t('inventory_detail_title_exempt_from_damage_waiver')} checked={isDamageWaiver} />

            <BaseCheckbox label={t('inventory_detail_title_discountable')} checked={isDiscountable} />

            <BaseCheckbox label={t('inventory_detail_title_taxable')} checked={isTaxable} />

            <BaseCheckbox label={t('inventory_detail_title_do_not_show_on_print')} checked={isShowOnPrint} />

            <BaseCheckbox label={t('inventory_detail_title_add_on_nly')} checked={isAddOn} />
          </Stack>
        </Grid2>
        <Grid2 size={12} className={cx('itemBox')}>
          <Stack gap={5}>
            <Typography className={cx('textHeader')}>{t('inventory_detail_title_description')}</Typography>

            <Typography className={cx('valueDescription')}>{detailProduct?.description || EMPTY_VALUE}</Typography>
          </Stack>
        </Grid2>
      </Grid2>
    </Grid2>
  );
};

export default InfoProductDetail;
